// frontend/shared-ui/src/components/SearchSelect.jsx
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

/* ─── helpers ──────────────────────────────────────────────── */

function defaultGetOptionValue(opt) {
  if (opt == null) return null;
  return typeof opt === "object" ? opt.value : opt;
}

function defaultGetOptionLabel(opt) {
  if (opt == null) return "";
  return typeof opt === "object" ? (opt.label ?? opt.value) : String(opt);
}

function isGrouped(options) {
  return (
    Array.isArray(options) &&
    options.length > 0 &&
    options[0] != null &&
    typeof options[0] === "object" &&
    Array.isArray(options[0].options)
  );
}

/* Flatten grouped options into a single list with a `groupIndex` on each. */
function flatten(options) {
  const out = [];
  if (!Array.isArray(options)) return out;

  if (!isGrouped(options)) {
    for (const opt of options) out.push({ opt, group: null });
    return out;
  }

  for (const group of options) {
    for (const opt of group.options || [])
      out.push({ opt, group: group.group });
  }
  return out;
}

/* ─── main component ──────────────────────────────────────── */
export default function SearchSelect({
  value,
  defaultValue,
  onChange,
  options = [],
  multiple = false,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyMessage = "No options",
  loading = false,
  disabled = false,
  clearable = true,
  getOptionValue = defaultGetOptionValue,
  getOptionLabel = defaultGetOptionLabel,
  renderOption,
  renderSelected,
  isOptionDisabled = (opt) => !!opt && opt.disabled === true,
  name,
  id,
  className = "",
  dropdownMaxHeight = 280,
  invalid = false,
  required = false,
  ariaLabel,
}) {
  /* ── state ─────────────────────────────────────────────── */
  const [internalValue, setInternalValue] = useState(
    value !== undefined
      ? value
      : defaultValue !== undefined
        ? defaultValue
        : multiple
          ? []
          : null,
  );
  const controlled = value !== undefined;
  const selectedValue = controlled ? value : internalValue;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const [dropdownPos, setDropdownPos] = useState(null);

  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  /* ── derived option list ───────────────────────────────── */
  const flat = useMemo(() => flatten(options), [options]);
  const grouped = useMemo(() => isGrouped(options), [options]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return flat;
    return flat.filter(({ opt }) => {
      const label = getOptionLabel(opt);
      const val = String(getOptionValue(opt) ?? "");
      return label.toLowerCase().includes(q) || val.toLowerCase().includes(q);
    });
  }, [flat, query, getOptionLabel, getOptionValue]);

  const selectedSet = useMemo(() => {
    const arr = multiple
      ? Array.isArray(selectedValue)
        ? selectedValue
        : []
      : [selectedValue];
    return new Set(arr.map((v) => String(v)));
  }, [multiple, selectedValue]);

  const selectedOptions = useMemo(() => {
    if (multiple) {
      const arr = Array.isArray(selectedValue) ? selectedValue : [];
      return arr.map((v) => {
        const found = flat.find(
          ({ opt }) => String(getOptionValue(opt)) === String(v),
        );
        return found ? found.opt : v;
      });
    }
    if (selectedValue == null) return null;
    const found = flat.find(
      ({ opt }) => String(getOptionValue(opt)) === String(selectedValue),
    );
    return found ? found.opt : selectedValue;
  }, [multiple, selectedValue, flat, getOptionValue]);

  /* ── positioning ───────────────────────────────────────── */
  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const dropH = Math.min(dropdownMaxHeight + 56, 340);
    const openUp = spaceBelow < dropH + 20 && spaceAbove > spaceBelow;

    setDropdownPos({
      left: rect.left,
      top: openUp ? Math.max(8, rect.top - dropH - 4) : rect.bottom + 4,
      width: rect.width,
      openUp,
    });
  }, [dropdownMaxHeight]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, updatePosition]);

  /* ── focus search on open ──────────────────────────────── */
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActiveIndex(filtered.length > 0 ? 0 : -1);
    const t = setTimeout(() => searchRef.current?.focus(), 10);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /* ── outside click ─────────────────────────────────────── */
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      // The dropdown is portaled — check its container too
      const dd = document.getElementById(`fh-search-select-${id || "__"}`);
      if (dd?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, id]);

  /* ── scroll active into view ───────────────────────────── */
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const list = listRef.current;
    if (!list) return;
    const active = list.querySelector(`[data-index="${activeIndex}"]`);
    active?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  /* ── value helpers ─────────────────────────────────────── */
  const commit = (next) => {
    if (!controlled) setInternalValue(next);
    onChange?.(next);
  };

  const selectOption = (opt) => {
    if (isOptionDisabled(opt)) return;
    const val = getOptionValue(opt);

    if (multiple) {
      const arr = Array.isArray(selectedValue) ? [...selectedValue] : [];
      const idx = arr.findIndex((v) => String(v) === String(val));
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(val);
      commit(arr);
      // keep dropdown open in multi mode
    } else {
      commit(val);
      setOpen(false);
      triggerRef.current?.focus();
    }
  };

  const clear = (e) => {
    e?.stopPropagation();
    commit(multiple ? [] : null);
  };

  /* ── keyboard ──────────────────────────────────────────── */
  const onTriggerKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true);
    }
  };

  const onSearchKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Home") {
      e.preventDefault();
      setActiveIndex(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setActiveIndex(filtered.length - 1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = filtered[activeIndex];
      if (item) selectOption(item.opt);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };

  /* ── trigger content ───────────────────────────────────── */
  const renderTriggerInner = () => {
    if (
      multiple &&
      Array.isArray(selectedOptions) &&
      selectedOptions.length > 0
    ) {
      return (
        <div className="fh-search-select__chips">
          {selectedOptions.map((opt, i) => {
            const val = getOptionValue(opt);
            return (
              <span key={String(val) + i} className="fh-search-select__chip">
                {renderSelected ? renderSelected(opt) : getOptionLabel(opt)}
                <button
                  type="button"
                  className="fh-search-select__chip-x"
                  onClick={(e) => {
                    e.stopPropagation();
                    const arr = (
                      Array.isArray(selectedValue) ? selectedValue : []
                    ).filter((v) => String(v) !== String(val));
                    commit(arr);
                  }}
                  aria-label="Remove"
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      );
    }
    if (!multiple && selectedOptions != null) {
      return (
        <span className="fh-search-select__value">
          {renderSelected
            ? renderSelected(selectedOptions)
            : getOptionLabel(selectedOptions)}
        </span>
      );
    }
    return <span className="fh-search-select__placeholder">{placeholder}</span>;
  };

  /* ── dropdown content ──────────────────────────────────── */
  const renderDropdown = () => {
    let lastGroup = null;
    let flatIdx = -1;

    return (
      <div
        id={`fh-search-select-${id || "__"}`}
        className="fh-search-select__dropdown"
        style={{
          position: "fixed",
          left: dropdownPos.left,
          top: dropdownPos.top,
          width: dropdownPos.width,
          zIndex: 10000,
        }}
        role="listbox"
        aria-multiselectable={multiple || undefined}
      >
        <div className="fh-search-select__search-wrap">
          <input
            ref={searchRef}
            type="text"
            className="fh-search-select__search"
            placeholder={searchPlaceholder}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={onSearchKeyDown}
            aria-label="Search options"
          />
          {query && (
            <button
              type="button"
              className="fh-search-select__search-clear"
              onClick={() => setQuery("")}
              tabIndex={-1}
            >
              ×
            </button>
          )}
        </div>

        <div
          ref={listRef}
          className="fh-search-select__list"
          style={{ maxHeight: dropdownMaxHeight, overflowY: "auto" }}
        >
          {loading ? (
            <div className="fh-search-select__empty">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="fh-search-select__empty">{emptyMessage}</div>
          ) : (
            filtered.map(({ opt, group }, idx) => {
              flatIdx++;
              const showGroupHeader = grouped && group !== lastGroup;
              lastGroup = group;

              const isSelected = selectedSet.has(String(getOptionValue(opt)));
              const isActive = flatIdx === activeIndex;
              const isDisabled = isOptionDisabled(opt);

              return (
                <React.Fragment key={`${idx}-${String(getOptionValue(opt))}`}>
                  {showGroupHeader && (
                    <div className="fh-search-select__group">{group}</div>
                  )}
                  <div
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={isDisabled || undefined}
                    data-index={flatIdx}
                    className={`fh-search-select__option ${
                      isActive ? "is-active" : ""
                    } ${isSelected ? "is-selected" : ""} ${
                      isDisabled ? "is-disabled" : ""
                    }`}
                    onMouseEnter={() => !isDisabled && setActiveIndex(flatIdx)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      if (!isDisabled) selectOption(opt);
                    }}
                  >
                    {renderOption ? (
                      renderOption(opt, { selected: isSelected })
                    ) : (
                      <>
                        <span className="fh-search-select__option-label">
                          {getOptionLabel(opt)}
                        </span>
                        {isSelected && (
                          <span className="fh-search-select__check">✓</span>
                        )}
                      </>
                    )}
                  </div>
                </React.Fragment>
              );
            })
          )}
        </div>
      </div>
    );
  };

  /* ── render ────────────────────────────────────────────── */
  const showClear =
    clearable &&
    !disabled &&
    (multiple
      ? Array.isArray(selectedValue) && selectedValue.length > 0
      : selectedValue != null && selectedValue !== "");

  return (
    <>
      <div
        ref={rootRef}
        className={`fh-search-select ${open ? "is-open" : ""} ${
          disabled ? "is-disabled" : ""
        } ${invalid ? "is-invalid" : ""} ${className}`}
      >
        <button
          ref={triggerRef}
          type="button"
          className="fh-search-select__trigger"
          onClick={() => !disabled && setOpen((v) => !v)}
          onKeyDown={onTriggerKeyDown}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={ariaLabel}
          aria-required={required || undefined}
        >
          <span className="fh-search-select__trigger-content">
            {renderTriggerInner()}
          </span>
          {showClear && (
            <span
              className="fh-search-select__clear"
              onClick={clear}
              role="button"
              tabIndex={-1}
              aria-label="Clear selection"
            >
              ×
            </span>
          )}
          <span
            className={`fh-search-select__chevron ${open ? "is-open" : ""}`}
            aria-hidden
          >
            ▾
          </span>
        </button>

        {name && (
          <input
            type="hidden"
            name={name}
            value={
              multiple
                ? (Array.isArray(selectedValue) ? selectedValue : []).join(",")
                : (selectedValue ?? "")
            }
          />
        )}
      </div>

      {open && dropdownPos && createPortal(renderDropdown(), document.body)}
    </>
  );
}
